import { action, isObservableMap, observable } from 'mobx';
import * as Prismic from '@prismicio/client';
import { CreatorDashboardModel } from './model';
import { EnvModel } from '../../env/model';
import { PrismicController } from '../../prismic/controller';
import { AnalyticsController } from '../../analytics/controller';
import { UIController } from '../../ui/controller';
import { APIController } from '../../api/controller';
import { UserModel } from '../../user/model';
import {
  CatalogueDataArray,
  NamedPrismicSlice,
  PrismicSlices,
  PrismicTypedDashboardHeaderProps,
} from '../../../components/project-happy/utilities/types';
import { AuthModel } from '../../auth/model';
import { PageModel } from '../model';
import { fetchSliceData } from '../../../components/project-happy/utilities/prismic';
import Router from 'react-router';
import { shuffleDashboardHeaderData } from '../../../components/project-happy/utilities/helpers';
import { isEmpty } from '../../../components/project-happy/utilities/objects';
import { BugsnagController } from '../../bugsnag/controller';

const DASHBOARD_HEADER_DOCUMENT_ID = 'creator_dashboard_header';
const DEFAULT_CONTENT_DOCUMENT_ID = 'creator_dashboard';

export class CreatorDashboardPageController {
  disposables: (() => void)[] = [];

  constructor(
    private page: PageModel,
    private model: CreatorDashboardModel,
    private env: EnvModel,
    private prismic: PrismicController,
    private api: APIController,
    private analytics: AnalyticsController,
    private ui: UIController,
    private user: UserModel,
    private auth: AuthModel,
    private bugsnag: BugsnagController
  ) {
    // These properties potentially come from SSR, where they will have been simplified to objects
    if (!isObservableMap(this.model.carousels)) {
      this.model.carousels = observable.map(this.model.carousels);
    }
    if (!isObservableMap(this.model.tracks)) {
      this.model.tracks = observable.map(this.model.tracks);
    }
    if (!isObservableMap(this.model.includedCarousels)) {
      this.model.includedCarousels = observable.map(this.model.includedCarousels);
    }
    if (!isObservableMap(this.model.includedTracks)) {
      this.model.includedTracks = observable.map(this.model.includedTracks);
    }
  }

  dispose() {
    this.disposables.forEach((disposer) => typeof disposer === 'function' && disposer());
  }

  @action
  onEnter: Router.EnterHook = async (nextState, replace) => {
    if (!this.env.isServer && !this.auth.token) {
      return replace({ pathname: '/' });
    }

    await this.env.ready;

    if (!this.model.contentLoaded) {
      const INCLUDED_CONTENT_DOCUMENT_ID =
        this.env.appEnv === 'prod' ? 'dashboard_included_production' : 'dashboard_included_test';

      const contentFilters = [
        Prismic.predicate.any('document.type', ['dashboard_header', 'dashboard_page', 'creator_dashboard']),
      ];

      try {
        const { results } = await this.prismic.query(contentFilters, { resolveImages: true });

        const headerContent = results.find((result) => result.uid === DASHBOARD_HEADER_DOCUMENT_ID);
        const defaultContent = results.find((result) => result.uid === DEFAULT_CONTENT_DOCUMENT_ID);
        const includedContent = results.find((result) => result.uid === INCLUDED_CONTENT_DOCUMENT_ID);

        const { body: headerResponse } = headerContent.data;

        this.model.defaultContentSlices = defaultContent.data.slices || [];
        this.model.includedContentSlices = includedContent.data.slices || [];

        const headerCarouselsData =
          headerResponse.length > 0
            ? headerResponse.map((b: { primary: PrismicTypedDashboardHeaderProps }) => ({
                ...b.primary,
                id: b.primary.title ? b.primary.title[0].text : '',
              }))
            : [];

        const dashboardHeaderSlice = {
          slice_type: PrismicSlices.DASHBOARD_HEADER,
          primary: {},
          items: headerCarouselsData.length ? shuffleDashboardHeaderData(headerCarouselsData) : [],
        } as NamedPrismicSlice<PrismicSlices.DASHBOARD_HEADER>;

        this.model.headerSlices = [dashboardHeaderSlice];
        this.model.slices = [...this.model.headerSlices];

        await this.populateContent();
      } catch (error) {
        // Handle errors fetching this content
        this.model.contentLoadError = true;
        this.bugsnag.notifyException(error);
      }
    }

    if (this.model.contentLoaded && !this.env.isServer) {
      this.setContentType();
    }

    await this.ui.setSEO('/dashboard');
  };

  @action
  onLeave() {
    if (!this.env.isServer) {
      this.dispose();
    }
  }

  /** Retrieve data for all page slices */
  @action
  populateContent = async () => {
    this.model.loadingCarouselData = true;

    const userRegion = this.user.user ? this.user.user.country : null;

    const [defaultContent, includedContent] = await Promise.all([
      fetchSliceData(this.api, this.env, this.model.defaultContentSlices, userRegion),
      fetchSliceData(this.api, this.env, this.model.includedContentSlices, userRegion),
    ]);

    this.model.carousels = observable.map<string, CatalogueDataArray>(defaultContent.carousels);
    this.model.tracks = observable.map(defaultContent.tracks);
    this.model.includedCarousels = observable.map<string, CatalogueDataArray>(includedContent.carousels);
    this.model.includedTracks = observable.map(includedContent.tracks);

    this.model.loadingCarouselData = false;
  };

  /**
   * Set the type of content to show, based on the user's catalogue preference (included / premium)
   *
   * Show default content if there's no user or the user has no preference set
   */
  @action
  setContentType = async () => {
    await this.user.ready;

    const showIncluded = !isEmpty(this.user.user) && this.user.user.preferred_catalogue_type === 'INCLUDED';

    if (showIncluded) {
      this.model.showIncludedContent = showIncluded;
      this.model.slices = [...this.model.headerSlices, ...this.model.includedContentSlices];
    } else {
      this.model.slices = [...this.model.headerSlices, ...this.model.defaultContentSlices];
    }
  };
}
